import { EVENT_META, FEED_TYPES, eventCategory, type BabyEvent, type EventCategory } from './meta.ts';

export interface StripSegment {
  category: EventCategory;
  /** Минуты от начала суток, 0–1440. */
  from: number;
  to: number;
}

export interface DaySummary {
  date: string;
  sleepMinutes: number;
  sleepCount: number;
  feedCount: number;
  bottleMl: number;
  diaperCount: number;
  walkMinutes: number;
  /** Последнее кормление (по началу), в т.ч. до начала суток. */
  lastFeed: { id: string; type: BabyEvent['type']; startedAt: string; minutesAgo: number } | null;
  /** Сколько минут малыш не спит (null, если сейчас спит или данных о сне нет). */
  awakeMinutes: number | null;
  /** Сколько минут длится текущий сон. */
  asleepMinutes: number | null;
  /** Идущие таймеры (запись без окончания). */
  running: BabyEvent[];
  strip: StripSegment[];
}

/** Минимальная ширина точечной записи на полосе суток, в минутах. */
const POINT_WIDTH = 12;

function overlapMinutes(a0: number, a1: number, b0: number, b1: number): number {
  return Math.max(0, Math.min(a1, b1) - Math.max(a0, b0)) / 60000;
}

/**
 * Сводка дня. `events` — записи, пересекающиеся с сутками, плюс записи за
 * предыдущие ~сутки (чтобы найти последнее кормление и конец сна).
 * `range` — границы суток в UTC (см. dayRange), `now` — текущий момент.
 */
export function summarizeDay(
  events: readonly BabyEvent[],
  range: { start: number; end: number },
  date: string,
  now: number,
): DaySummary {
  const dayEnd = Math.min(range.end, Math.max(now, range.start));
  const dayLen = (range.end - range.start) / 60000;
  const toMin = (ms: number) => Math.min(dayLen, Math.max(0, (ms - range.start) / 60000));

  let sleepMinutes = 0;
  let sleepCount = 0;
  let feedCount = 0;
  let bottleMl = 0;
  let diaperCount = 0;
  let walkMinutes = 0;
  const strip: StripSegment[] = [];
  const running: BabyEvent[] = [];

  let lastFeed: DaySummary['lastFeed'] = null;
  let lastSleepEnd: number | null = null;
  let currentSleepStart: number | null = null;

  const sorted = [...events].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt));

  for (const e of sorted) {
    // Укладывание ещё не сон: в итоги не идёт, но таймер виден как идущий.
    if (e.type === 'sleep' && e.payload.settling) {
      if (e.endedAt === null) running.push(e);
      continue;
    }
    const s = Date.parse(e.startedAt);
    const end = e.endedAt ? Date.parse(e.endedAt) : null;
    const meta = EVENT_META[e.type];
    // У точечной записи (бутылочка, подгузник) нет длительности: она живёт в момент начала.
    const effEnd = end ?? (meta.timed ? now : s);
    const startsInDay = s >= range.start && s < range.end;
    const inDay = meta.timed ? s < range.end && effEnd > range.start : startsInDay;

    if (end === null && meta.timed) running.push(e);

    if (FEED_TYPES.includes(e.type) && s <= now) {
      if (!lastFeed || s >= Date.parse(lastFeed.startedAt)) {
        lastFeed = { id: e.id, type: e.type, startedAt: e.startedAt, minutesAgo: Math.round((now - s) / 60000) };
      }
    }

    if (e.type === 'sleep') {
      if (end === null) currentSleepStart = s;
      else if (end <= now && (lastSleepEnd === null || end > lastSleepEnd)) lastSleepEnd = end;
    }

    if (!inDay) continue;

    if (startsInDay) {
      if (FEED_TYPES.includes(e.type)) feedCount += 1;
      if (e.type === 'bottle' && typeof e.payload.ml === 'number') bottleMl += e.payload.ml;
      if (e.type === 'diaper') diaperCount += 1;
      if (e.type === 'sleep') sleepCount += 1;
    }
    if (e.type === 'sleep') sleepMinutes += overlapMinutes(s, effEnd, range.start, dayEnd);
    if (e.type === 'walk') walkMinutes += overlapMinutes(s, effEnd, range.start, dayEnd);

    const cat = eventCategory(e);
    if (cat === 'other') continue;
    const from = toMin(s);
    let to = meta.timed ? toMin(Math.min(effEnd, dayEnd)) : Math.min(dayLen, from + POINT_WIDTH);
    // Короткая таймерная запись (бутылочка за 5 минут) должна быть видна на полосе.
    if (meta.timed && startsInDay && to - from < POINT_WIDTH) to = Math.min(dayLen, from + POINT_WIDTH);
    if (to > from || !meta.timed) strip.push({ category: cat, from, to: Math.max(to, from + 1) });
  }

  return {
    date,
    sleepMinutes: Math.round(sleepMinutes),
    sleepCount,
    feedCount,
    bottleMl,
    diaperCount,
    walkMinutes: Math.round(walkMinutes),
    lastFeed,
    awakeMinutes: currentSleepStart !== null || lastSleepEnd === null ? null : Math.round((now - lastSleepEnd) / 60000),
    asleepMinutes: currentSleepStart !== null ? Math.round((now - currentSleepStart) / 60000) : null,
    running,
    strip,
  };
}

/** Записи, которые «занимают» время: сон, кормления с длительностью, прогулки, бодрствование, плач, свои события с длительностью. */
function covers(e: BabyEvent): boolean {
  if (!EVENT_META[e.type].timed) return false;
  if (e.type === 'sleep' && e.payload.settling) return true; // укладывание — тоже занятое время
  return true;
}

export interface Gap {
  from: number;
  to: number;
  minutes: number;
}

/**
 * Неотмеченные промежутки в [from, to): время, которое не покрыто ни одной записью с длительностью.
 * Короткие (меньше minMinutes) не считаем — это переходы между делами.
 */
export function findGaps(events: readonly BabyEvent[], from: number, to: number, now: number, minMinutes = 15): Gap[] {
  const iv: [number, number][] = [];
  for (const e of events) {
    if (!covers(e)) continue;
    const s = Date.parse(e.startedAt);
    const end = e.endedAt ? Date.parse(e.endedAt) : now;
    const a = Math.max(from, s);
    const b = Math.min(to, end);
    if (b > a) iv.push([a, b]);
  }
  iv.sort((x, y) => x[0] - y[0]);
  const gaps: Gap[] = [];
  let cur = from;
  const push = (a: number, b: number) => {
    if (b - a >= minMinutes * 60000) gaps.push({ from: a, to: b, minutes: Math.round((b - a) / 60000) });
  };
  for (const [a, b] of iv) {
    if (a > cur) push(cur, a);
    cur = Math.max(cur, b);
  }
  push(cur, Math.min(to, now));
  return gaps;
}
